import { createColumnHelper } from '@tanstack/react-table'
import { Container, Mail, Plus, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import { RecordId, RowValue } from '@/components/data/DataTable'
import { FacetFilter, FilterEmptyState } from '@/components/data/FacetFilter'
import { RecordBoard } from '@/components/data/RecordBoard'
import {
  toHiddenColumns,
  toVisibilityState,
  useViewState,
} from '@/components/data/useViewState'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { EmptyState } from '@/components/feedback/EmptyState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { toOptions } from '@/components/ui/control'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { useFormat } from '@/i18n/useFormat'
import { placeName } from '@/lib/countries'
import { formatMoney, formatNumber } from '@/lib/format'
import { MODE_ICONS, modeLabelKey } from '@/lib/icons'
import { CASE_TONES } from '@/lib/statusTone'
import { ApiError } from '@/lib/api'
import { can, canAll, CAP, reaches, SURFACE } from '@/lib/can'
import { brandingRefusalFields, brandingRefusalMessage } from '@/lib/refusals'
import { useSession } from '@/features/auth/api/useSession'
import { ErrorState } from '@/components/feedback/ErrorState'
import {
  canTransitionCase,
  toCasePayload,
  useCases,
  useCreateCase,
  useSendClientStatuses,
  useTransitionCase,
} from '@/features/cases/api/useCase'
import { CaseCard } from '@/features/cases/components/CaseCard'
import { CaseFormSheet } from '@/features/cases/components/CaseFormSheet'
import { LegPlace } from '@/features/cases/components/legs'
import { profitSign } from '@/features/cases/lib/profit'
import { ClientStatusSendSheet } from '@/features/cases/components/ClientStatusSendSheet'
import { Skeleton } from '@/components/feedback/Skeleton'
import type { CaseStatus, CaseSummary } from '@/types/domain'

/* The case list — the DENSE regime. Sales and Operations move through hundreds
 * of rows and the product must not be slower than the Excel it replaces.
 *
 * "Create a case without a quote" (FR-70) is a PRIMARY action here: repeat
 * business runs through it, and so do all ~241 migrated cases, which arrive
 * quote-less by definition. A case FROM a quote is made only by converting the
 * quote (S-1, owner decision O4): the conversion names the Operations owner
 * and carries the terms, the costs and the leg, and closes the quote. The
 * «Из котировки» action that used to sit here opened this page's plain create
 * form with a quote picker, never sent the link, and left the quote Sent. */

const column = createColumnHelper<CaseSummary>()

const VIEW_OPTIONS = [
  { value: 'table', labelKey: 'common.view.table' },
  { value: 'board', labelKey: 'common.view.board' },
] as const

/* What a lane is WORTH, at the right of its heading: the sum of the rates in it.
 * The RATE rather than the forecast, because the question a pipeline is read for
 * is how much freight is in this state, and a lane's profit is a figure the
 * money screens answer properly.
 *
 * Mixed currencies are summed as if they matched, which would be a lie — so the
 * total is withheld instead, exactly as the quotes board withholds its own.
 * Today every case is USD (AD-19's sibling rule on money), and this is the guard
 * for the day that stops being true. */
function laneTotal(rows: CaseSummary[]) {
  const priced = rows.filter((row) => row.rate)
  if (priced.length === 0) return null

  const currency = priced[0]?.rate?.currency ?? 'USD'
  if (priced.some((row) => row.rate?.currency !== currency)) return null

  const sum = priced.reduce((acc, row) => acc + Number(row.rate?.amount ?? 0), 0)
  return formatMoney(sum, currency, 0)
}

/** Board lanes in pipeline order: the working states first, the ends last. */
const CASE_LANES: readonly CaseStatus[] = [
  'draft',
  'in-progress',
  'stuck',
  'delivered',
  'operationally-closed',
  'financially-closed',
  'cancelled',
]

/** "Nothing selected", as one shared array — the quotes surface holds the same
 *  constant for the same reason. A fresh `[]` per render would make the row
 *  memo below recompute on every paint and defeat itself; the hook already
 *  DELETES an empty selection rather than storing one, so an absent facet and
 *  an empty one are the same fact and may share an object. */
const NO_FILTER: readonly string[] = []

/* The case list, behind its surface (#129, N-08). A role at «Нет доступа» on
 * cases has no Cases stop in the menu; a deep link here gets one refusal
 * rather than a list the server would answer empty. The list lives in its own
 * component so no list query fires for a refused reader. Reach is the
 * session's (`reaches()`); the server scopes the rows regardless (AD-21). */
export function CasesPage() {
  const { t } = useTranslation()
  const { data: session, isPending: sessionPending } = useSession()

  if (sessionPending) {
    return (
      <>
        <PageHeader title={t('cases.title')} subtitle={t('cases.subtitle')} />
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }

  if (!reaches(session, SURFACE.cases)) {
    return (
      <>
        <PageHeader title={t('cases.title')} />
        <Card>
          <EmptyState
            title={t('cases.noAccess.title')}
            description={t('cases.noAccess.description')}
          />
        </Card>
      </>
    )
  }

  return <CaseList />
}

function CaseList() {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [sendingStatus, setSendingStatus] = useState(false)
  /* What the last bulk send reached. Held on the page rather than in the sheet
     because the sheet closes on success — a confirmation shown only inside it
     would be shown only to a form nobody is looking at any more. */
  const [sentSummary, setSentSummary] = useState<{
    sent: number
    total: number
  } | null>(null)
  /* View, hidden columns, sort and filter together, remembered per user and per
     surface in localStorage (UX-DR-4, and decision §2 of this issue). The key
     carries the user id as well as the surface because two people share the
     terminal in the Türkmenbaşy office and neither should inherit the other's
     narrowed list. No preferences endpoint is called for any of it: none exists
     beyond PATCH /session/language, and these are a workstation habit rather
     than a profile setting. */
  const [viewState, controls] = useViewState('cases')
  /* The real list (GET /cases), not the fixtures this page used to render. The
     rows were MOCK_CASES while the create wrote actual database rows, so a case
     the desk had just opened never appeared in the list it was opened from. */
  const { data, isPending, isError, error, refetch } = useCases()
  const cases = data ?? []
  const create = useCreateCase()

  /* Every row opens now. GET /cases/:id moved to the AD-23 read composer
     (cmd/asa/readmodel_case.go), so a real case answers with its record, its
     route and its files — the CASE_DETAIL_READY gate that once held rows in
     the list is gone with the 404 it guarded against. */
  const openCase = (row: CaseSummary) => {
    navigate(`/cases/${row.id}`)
  }
  const sendStatuses = useSendClientStatuses()

  /* What a refused send SAYS, and the one refusal that must not be worded as a
     retry.
     `cases.statusSend.failed` — "check the connection and try again" — is the
     honest sentence for what this endpoint usually fails on: a dead network, a
     500, a shipment that moved while the sheet was open. It is the wrong
     sentence for exactly one refusal. FR-64's branding gap comes back as a 422
     naming the ISSUING ENTITY's missing fields (internal/casefile's `fail`,
     which answers what internal/quote and internal/document already answered),
     and retrying fixes none of it — the cure is a settings form nobody filled
     in, on a screen this page never mentions.
     Read through `lib/refusals` rather than by inspecting `problem.errors`
     here: the offer sheet, the invoice sheet and the case's document card all
     name these fields from the same document, and a fourth reading of one wire
     shape is how two surfaces end up disagreeing about which field was
     missing. `brandingRefusalFields` answers null for everything else, which is
     what keeps the sentence above intact for every other failure — this is not
     the generic problem mapping quietly taking the message over.
     One more refusal is not a retry either (story CI, R9/R10): a 403 means
     the caller lacks `directory.contact.read` — the send would hand them the
     client's address — and the sheet already says so up front; this is the
     stale tab whose role was narrowed after the page loaded. It is worded as
     who can send, never as "check the connection". */
  const sendRefusal = brandingRefusalFields(sendStatuses.error)
  const sendForbidden =
    sendStatuses.error instanceof ApiError && sendStatuses.error.isForbidden
  /* The sender, for the sheet's one capability question: whether they may
     read the client's contacts. */
  const { data: user } = useSession()
  /* The two header acts, each absent without what the server checks (#128,
     N-07): a new case needs `case.create`; a status send needs `case.create`
     AND `directory.contact.read` — the order casefile's `statusCallerID`
     refuses in, since the send answers with the contact's address. A role
     without them would fill a form whose submit is a 403. */
  const mayCreate = can(user, CAP.caseCreate)
  const maySendStatus = canAll(user, CAP.caseCreate, CAP.directoryContactRead)
  /* Dragging a card between lanes IS the status change.
     The move applies at once and rolls back if the server refuses — the
     refusal is worth saying out loud rather than letting the card slide back
     unexplained, which reads as a dropped gesture rather than a rule. */
  const transition = useTransitionCase()
  const [refusedMove, setRefusedMove] = useState<string | null>(null)

  const columns = useMemo(
    () => [
      column.accessor('number', {
        header: t('cases.columns.number'),
        size: 96,
        cell: (info) => <RecordId>{info.getValue()}</RecordId>,
      }),
      column.accessor('clientName', {
        header: t('cases.columns.client'),
        cell: (info) => (
          <RowValue
            primary={info.getValue()}
            secondary={info.row.original.ownerName}
          />
        ),
      }),
      column.accessor('destination', {
        header: t('cases.columns.route'),
        cell: (info) => {
          const { origin, destination, mode } = info.row.original
          /* A case may have no mode at all (a NULL column, P0-2): no icon, a
             dash for the label, and a blank of the icon's width so the places
             still line up down the column. */
          const Icon = mode ? MODE_ICONS[mode] : undefined
          return (
            <span className="flex items-center gap-2">
              {Icon ? (
                <Icon size={15} className="text-text-muted shrink-0" aria-hidden />
              ) : (
                <span className="w-[15px] shrink-0" aria-hidden />
              )}
              <RowValue
                primary={
                  <>
                    {placeName(origin, i18n.language)}
                    {/* A decorative separator: both places are named beside it. */}
                    <span className="text-text-faint mx-1.5">→</span>
                    {placeName(destination, i18n.language)}
                  </>
                }
                secondary={mode ? t(modeLabelKey(mode)) : '—'}
              />
            </span>
          )
        },
      }),
      /* WHERE THE CARGO IS, beside where it is going. The column the board card
         already had and the table did not, which made the two views of one list
         answer different questions: the board said "leg 2 of 3, waiting at a
         border" and the table, on the same row, said only that the case was in
         progress.

         The leg's own state is spelled out in the second line rather than
         tinted into the icon. The dense regime already carries a coloured pill
         one column along and a health marker at the row's end, and a third
         colour competing with those is how a scan stops finding either. The
         board tints its disc because a card has room to be looked at; a row is
         read. */
      column.display({
        id: 'currentLeg',
        header: t('cases.columns.leg'),
        size: 188,
        cell: (info) => {
          const leg = info.row.original.currentLeg
          // A route nobody has planned, and one that has finished: both answer
          // with no position, and a "leg 0" would be a number nobody can act on.
          if (!leg) return <span className="text-text-faint">—</span>

          const LegIcon = MODE_ICONS[leg.mode]
          const position = t('cases.card.leg', {
            sequence: leg.sequence,
            total: leg.total,
          })
          const state = t(`legStatus.${leg.status}`)
          // A leg whose ends nobody typed still has a position and a state, and
          // says them instead of leaving the cell blank.
          const routed = Boolean(leg.origin || leg.destination)

          return (
            <span className="flex items-center gap-2">
              <LegIcon
                size={15}
                className="text-text-muted shrink-0"
                aria-hidden
              />
              <RowValue
                primary={
                  routed ? (
                    <>
                      <LegPlace name={leg.origin ?? ''} country={leg.originCountry} />
                      <span className="text-text-faint mx-1.5">→</span>
                      <LegPlace
                        name={leg.destination ?? ''}
                        country={leg.destinationCountry}
                      />
                    </>
                  ) : (
                    position
                  )
                }
                secondary={routed ? `${position} · ${state}` : state}
              />
            </span>
          )
        },
      }),
      column.accessor('status', {
        header: t('cases.columns.status'),
        size: 132,
        cell: (info) => <CaseStatusPill status={info.getValue()} />,
      }),
      column.accessor('eta', {
        header: t('cases.columns.eta'),
        size: 112,
        // Right-aligned like a figure, but NOT tabular: a date is read, not
        // scanned down the column, and tabular figures pad its spaces to a
        // digit's width, which pulls "4 Aug 2026" apart.
        meta: { headerClassName: 'text-right', cellClassName: 'text-right' },
        cell: (info) => format.date(info.getValue()),
      }),
      column.accessor((row) => row.rate?.amount, {
        id: 'rate',
        header: t('cases.columns.rate'),
        size: 116,
        meta: { numeric: true },
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      /* The row's ONE profit (S-6): the forecast while the case runs, the cash
         once it is financially closed — the server's `profit`, the same figure
         as the right side of the case's money line. Never summed here. */
      column.accessor((row) => row.profit?.amount, {
        id: 'profit',
        header: t('cases.columns.profit'),
        size: 128,
        meta: { numeric: true },
        cell: (info) => {
          const value = info.getValue()
          const negative = profitSign(value) === 'loss'
          return (
            // A loss is marked by weight and sign as well as colour.
            <span className={negative ? 'text-negative font-semibold' : undefined}>
              {formatNumber(value, 2)}
            </span>
          )
        },
      }),
      // The trailing flag column stays EMPTY on healthy rows. Empty space in a
      // healthy row is what makes an unhealthy row visible.
      column.display({
        id: 'flag',
        header: '',
        meta: { flag: true },
        cell: (info) => {
          const { daysStalled } = info.row.original
          if (!daysStalled) return null
          return (
            <span
              title={t('cases.stalled', { count: daysStalled })}
              className="text-attention flex justify-center"
            >
              <TriangleAlert size={15} aria-hidden />
              <span className="sr-only">
                {t('cases.stalled', { count: daysStalled })}
              </span>
            </span>
          )
        },
      }),
    ],
    [t, format, i18n.language],
  )

  /* What the columns menu offers, by the ids the table above knows them as —
     a label here that no column answers to would be a switch wired to nothing.
     `number` is LOCKED: a case list with the case number hidden is a grid of
     anonymous rows, and the way back to it is the menu the user has just lost
     the ability to recognise the list from. The `flag` display column is not
     offered at all — the stalled marker is the row's HEALTH rather than one of
     its fields, and a health signal a user can switch off is a health signal
     nobody sees. */
  /* Applied to the STORED hidden list as well as to the menu below, because
     localStorage is user-writable and a release where a column was still
     optional leaves real entries behind. A stored "hide the number" would
     otherwise hide the identifier with no way back: the menu renders a locked
     entry ticked and disabled, so it cannot be un-hidden there, and if it were
     the only hidden column the reset button would have nothing to count. */
  const menuColumns = useMemo(
    () => [
      { id: 'number', label: t('cases.columns.number'), locked: true },
      { id: 'clientName', label: t('cases.columns.client') },
      { id: 'destination', label: t('cases.columns.route') },
      { id: 'currentLeg', label: t('cases.columns.leg') },
      { id: 'status', label: t('cases.columns.status') },
      { id: 'eta', label: t('cases.columns.eta') },
      { id: 'rate', label: t('cases.columns.rate') },
      { id: 'profit', label: t('cases.columns.profit') },
    ],
    [t],
  )

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter(
          (id) => !menuColumns.some((entry) => entry.locked && entry.id === id),
        ),
      ),
    [viewState.hiddenColumns, menuColumns],
  )

  /* The status facet offers the same lanes the board draws, in the same order,
     so the two ways of asking "where are my cases" name the states identically. */
  const statusOptions = useMemo(
    () => toOptions(CASE_LANES, (status) => t(`caseStatus.${status}`)),
    [t],
  )
  const statusFilter = viewState.filters.status ?? NO_FILTER

  /* A VIEW NARROWING, and nothing more (AD-5, AD-21). `caseScope` has already
     decided in SQL which cases this caller may see, so this filter only ever
     hides rows the server chose to send. It cannot widen: there is no "show
     all" toggle, an empty selection means the unnarrowed server list rather
     than everything in the database, and NO filter value is forwarded to
     GET /cases as a query parameter — a parameter that could widen scope is
     exactly what backend/internal/enforcement_test.go scans the route table
     for. The client is not a decision point about who sees what.

     It is applied only where rows are RENDERED. The client-status send sheet
     below still chooses from the whole list: narrowing one's own view is not a
     statement about which clients should be told where their shipments are.

     Memoised on the same grounds the quotes surface memoises its rows: the
     table below takes this array by identity, so a fresh one built by an
     unrelated re-render — a toast, a sheet opening — makes TanStack rebuild a
     sorted row model over rows that did not change. */
  const visible = useMemo(() => {
    const all = data ?? []
    if (statusFilter.length === 0) return all
    return all.filter((row) => statusFilter.includes(row.status))
  }, [data, statusFilter])

  /* The PHONE list's card — three fields and the status pill, which is the whole
     budget on a narrow screen. The board no longer shares it: a lane is scanned
     for "can this move on today", and that question needs the money, the dates
     and the owner, which is what CaseCard puts on the board (and why it leaves
     the pill off — the lane already says the status).
     The stalled flag stays on BOTH: it is the row's health, not its status. */
  const listCard = (row: CaseSummary) => (
    <RecordCard
      id={row.number}
      status={<CaseStatusPill status={row.status} />}
      primary={row.clientName}
      secondary={[placeName(row.origin, i18n.language), placeName(row.destination, i18n.language)]
        .filter(Boolean)
        .join(' → ')}
      value={formatNumber(row.profit?.amount, 2)}
      valueLabel={t('cases.card.profit')}
      flag={
        row.daysStalled ? (
          <span className="text-attention flex items-center gap-1 text-[11px] font-medium">
            <TriangleAlert size={13} aria-hidden />
            {t('cases.card.stalledShort', { count: row.daysStalled })}
          </span>
        ) : null
      }
    />
  )

  /* What an EMPTY lane says on the desktop board. The invitation is conditional
     because it is only true of a lane something in this list may actually be
     dragged into: printing "drag one here" over Cancelled would teach a gesture
     the lifecycle table (AD-24) refuses. */
  const laneEmpty = (lane: CaseStatus) => {
    const droppable = visible.some((row) => canTransitionCase(row.status, lane))
    return (
      <div className="flex flex-col gap-1">
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('cases.board.laneEmpty')}
        </p>
        {droppable ? (
          <p className="text-text-faint text-[12px] leading-snug font-light">
            {t('cases.board.laneDrop')}
          </p>
        ) : null}
      </div>
    )
  }

  /* Two different facts, and they must not borrow each other's copy. "You have
     no cases" over a list a filter has just emptied tells the desk its work has
     disappeared; the filter's own empty state names what happened and carries
     the one press that undoes it. The `cases.length > 0` guard is what keeps
     the distinction honest in the other direction — a filter over an empty list
     narrowed nothing, so the surface's own empty state is still the true one. */
  const empty =
    statusFilter.length > 0 && cases.length > 0 ? (
      <FilterEmptyState onClear={() => controls.clearFilters()} />
    ) : (
      <EmptyState
        icon={Container}
        title={t('cases.emptyTitle')}
        description={t('cases.emptyDescription')}
      />
    )

  return (
    <>
      <PageHeader
        title={t('cases.title')}
        subtitle={t('cases.subtitle')}
        actions={
          <>
            <SegmentedControl
              label={t('common.view.label')}
              size="md"
              options={VIEW_OPTIONS}
              value={viewState.view}
              onChange={controls.setView}
            />
            {/* The filter sits beside the view toggle rather than over the
                table, because it narrows BOTH views and a control that lived on
                one of them would look like it belonged to that one. */}
            <FacetFilter
              label={t('common.filters.status')}
              options={statusOptions}
              selected={statusFilter}
              onChange={(values) => controls.setFilter('status', values)}
            />
            {/* Only the table has columns. Offering the menu on the board would
                be offering switches that change nothing on screen, which teaches
                the user the control is broken rather than that it is elsewhere;
                the choice itself is still remembered while the board is open. */}
            {viewState.view === 'table' ? (
              <ColumnsMenu
                columns={menuColumns}
                hidden={viewState.hiddenColumns}
                onToggle={controls.toggleColumn}
                onReset={() => controls.setHiddenColumns([])}
              />
            ) : null}
            {/* Telling a client where their shipments are, from the list
                (FR-41). Secondary, like the other non-primary header actions:
                this page is for reading cases, and Gateway Blue belongs to the
                one action it is FOR. */}
            {maySendStatus ? (
              <Button
                variant="secondary"
                iconLeft={Mail}
                onClick={() => {
                  sendStatuses.reset()
                  setSentSummary(null)
                  setSendingStatus(true)
                }}
              >
                {t('cases.actions.sendStatus')}
              </Button>
            ) : null}
            {mayCreate ? (
              <Button iconLeft={Plus} onClick={() => setCreating(true)}>
                {t('cases.actions.newCase')}
              </Button>
            ) : null}
          </>
        }
      />

      {/* Names how many went and how many were attempted, rather than a bare
          "sent": a batch is not atomic, and four of five is the fact the sender
          needs. `partial` reads differently from `all` on purpose. */}
      {sentSummary ? (
        <div
          role="status"
          className="border-positive/30 bg-positive-soft text-positive mb-4 rounded-[var(--radius-md)] border px-4 py-2.5 text-[13px]"
        >
          {sentSummary.sent === sentSummary.total
            ? t('cases.clientStatusSend.sentAll', { count: sentSummary.sent })
            : t('cases.clientStatusSend.sentPartial', {
                sent: sentSummary.sent,
                total: sentSummary.total,
              })}
        </div>
      ) : null}

      {/* A refused move. The card has already returned to its lane, so this
          says WHICH case would not move and why the server refused — an
          unsettled balance blocking a financial close is the refusal somebody
          can actually act on (FR-25). */}
      {refusedMove ? (
        <div
          role="status"
          className="border-attention/30 bg-attention-soft text-attention mb-4 rounded-[var(--radius-md)] border px-4 py-2.5 text-[13px]"
        >
          {t('cases.board.moveRefused', { number: refusedMove })}
        </div>
      ) : null}

      <Card>
        {isPending ? (
          <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : viewState.view === 'board' ? (
          <RecordBoard
            label={t('cases.title')}
            data={visible}
            /* The lane's tint comes from the SAME map the pill in its heading
               reads, so the colour of a column and the colour of the label at
               the top of it cannot part company. */
            lanes={CASE_LANES.map((status) => ({
              value: status,
              header: <CaseStatusPill status={status} />,
              tone: CASE_TONES[status],
            }))}
            groupBy={(row) => row.status}
            getRowId={(row) => row.id}
            onCardClick={(row) => openCase(row)}
            renderCard={(row) => <CaseCard case={row} />}
            /* Only a legal move lights up a lane. The lifecycle table says
               which (AD-24) — so "a cancelled case does not go back to work"
               is said by the lane refusing the card, before the drop, rather
               than by an error after it. */
            canMoveTo={(row, lane) => canTransitionCase(row.status, lane)}
            onMoveCard={(row, lane) => {
              setRefusedMove(null)
              transition.mutate(
                { caseId: row.id, status: lane },
                {
                  /* The card is already back where it started — the mutation
                     rolls the list back. This names the case, because by the
                     time the message is read the card has moved and "that
                     move was refused" about an unnamed card is not actionable. */
                  onError: () => setRefusedMove(row.number),
                },
              )
            }}
            laneTotal={laneTotal}
            laneEmpty={laneEmpty}
            empty={empty}
          />
        ) : (
        /* Below the table breakpoint a row stops being a row and becomes a
            card carrying the two or three highest-priority fields. Scrolling a
            grid sideways on a phone is prohibited anywhere in the product. */
        <RecordList
          label={t('cases.title')}
          data={visible}
          columns={columns}
          getRowId={(row) => row.id}
          onRowClick={(row) => openCase(row)}
          renderCard={listCard}
          empty={empty}
          /* Sort and column visibility are CONTROLLED here so that both
             survive leaving the page: the table keeps its own copy when a
             caller passes neither, and a table that remembered them itself
             would forget them on unmount. */
          sorting={viewState.sorting}
          onSortingChange={controls.setSorting}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={(next) =>
            controls.setHiddenColumns(toHiddenColumns(next))
          }
        />
        )}
      </Card>

      {/* A refused create renders beside the list, and the sheet stays open
          holding what was typed rather than discarding it (UX-DR-18). */}
      {create.error ? (
        <ErrorState error={create.error} onRetry={() => create.reset()} />
      ) : null}

      {/* Pick a client, pick their cases or all of them, send once (FR-41).
          Stays open on failure holding the note, and closes only once the
          server has accepted — an email cannot be unsent. */}
      <ClientStatusSendSheet
        open={sendingStatus}
        onClose={() => setSendingStatus(false)}
        cases={cases}
        user={user}
        pending={sendStatuses.isPending}
        submitError={
          !sendStatuses.isError
            ? undefined
            : sendRefusal !== null
              ? brandingRefusalMessage(sendRefusal, t)
              : sendForbidden
                ? t('cases.statusSend.noContactAccess')
                : t('cases.statusSend.failed')
        }
        onSubmit={(values) =>
          sendStatuses.mutate(values, {
            onSuccess: (result) => {
              setSentSummary({ sent: result.sent, total: result.total })
              setSendingStatus(false)
            },
          })
        }
      />

      <CaseFormSheet
        open={creating}
        onClose={() => setCreating(false)}
        pending={create.isPending}
        onSubmit={(draft) =>
          create.mutate(toCasePayload(draft), {
            // Closed from onSuccess, so a refusal leaves the sheet open with the
            // draft intact rather than closing over a save that never happened.
            onSuccess: (record) => {
              setCreating(false)
              /* Onto the case just created where that is possible (UJ-1: the
                 header is where the work starts). Where it is not — which is
                 every real case until the detail route is real — the list says
                 the case was opened and names its number, rather than throwing
                 the user at a 404 that reads as a failed save. */
              openCase(record)
            },
          })
        }
      />
    </>
  )
}
